// components/Header.tsx
'use client';

import Image from 'next/image';
import NextLink from 'next/link';
import { useEffect, useState } from 'react';
import { useScrollContainer } from '@/app/_lib/providers/ScrollProvider';
import { ThemeAwareLogo } from './ThemeAwareLogo';

interface HeaderProps {
  isInnerPage?: boolean;
}

const Header = ({ isInnerPage = false }: HeaderProps) => {
  const appName = process.env.NEXT_PUBLIC_APP_NAME || '';
  const facilityTelephone = process.env.NEXT_PUBLIC_FACILITY_TELEPHONE || '';
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { scrollContainerRef } = useScrollContainer();

  // Determine initial scrolled state for inner pages
  useEffect(() => {
    if (isInnerPage) {
      setIsScrolled(true);
    }
  }, [isInnerPage]);

  const scrollToTop = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
    setIsMobileMenuOpen(false);
  };

  const handleToggleMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const removeActiveClass = () => {
    setIsMobileMenuOpen(false);
  };

  useEffect(() => {
    if (!isInnerPage && scrollContainerRef.current) {
      // For non-inner pages, use scroll position relative to carousel height
      const handleScroll = () => {
        const scrollPosition = scrollContainerRef.current?.scrollTop || 0;
        const carousel = document.getElementById('hero-slideshow');
        const carouselHeight = carousel?.offsetHeight || 0;

        // Change header styling after scrolling past the carousel
        setIsScrolled(scrollPosition > carouselHeight);
      };

      // Add scroll event listener to the scroll container
      const scrollContainer = scrollContainerRef.current;
      scrollContainer.addEventListener('scroll', handleScroll);

      // Initial check
      handleScroll();

      return () => {
        scrollContainer.removeEventListener('scroll', handleScroll);
      };
    }
  }, [isInnerPage, scrollContainerRef]);

  useEffect(() => {
    // Always add resize listener
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []); // Empty dependency array

  // Navigation items for reuse
  const navItems = [
    { id: 'history', label: 'History' },
    { id: 'vision', label: 'Vision' },
    { id: 'locations', label: 'Our Communities' },
    { id: 'contact-us', label: 'Contact Us' },
    { id: 'jobs', label: 'Job Opportunities' },
  ];

  const headerBgClass = 'bg-fuchsia-300/80 dark:bg-gray-900/90';
  const textColorClass = 'text-primary dark:text-gray-200';
  const phoneTextColorClass = 'text-primary hover:text-primary-dark dark:text-gray-200 dark:hover:text-white';
  const navOpacityClass = isScrolled || isInnerPage ? 'opacity-100' : 'opacity-95';

  return (
    <header
      className={`fixed top-0 right-0 left-0 z-[2500] transition-all duration-300 ease-in-out ${headerBgClass} backdrop-blur-xl`}
    >
      {/* Top Banner Section */}
      <div className='container mx-auto px-4 sm:px-6 lg:px-8'>
        <div className='relative flex h-20 items-center justify-between overflow-hidden md:h-24'>
          {/* Left Side: Logo */}
          <div className='flex h-full max-h-full w-1/2 flex-none items-center overflow-hidden md:w-1/2 xl:w-2/6'>
            {/* Desktop Logo */}
            {isInnerPage ? (
              <NextLink
                href='/'
                aria-label='Return to homepage'
                className='hidden h-full w-full max-w-full cursor-pointer items-center md:flex'
              >
                <ThemeAwareLogo className='h-full max-w-full object-contain' />
              </NextLink>
            ) : (
              <NextLink
                href='#main-content'
                aria-label='Return to top of the page'
                onClick={(e) => {
                  e.preventDefault();
                  scrollToTop();
                }}
                className='hidden h-full w-full max-w-full cursor-pointer items-center md:flex'
              >
                <ThemeAwareLogo className='h-full max-w-full object-contain' />
              </NextLink>
            )}

            {/* Mobile Logo */}
            {isInnerPage ? (
              <NextLink href='/' className='flex cursor-pointer items-center md:hidden' aria-label='Return to homepage'>
                <div className='relative flex aspect-[971/892] h-[50px] w-[55px] items-center gap-y-1 pr-2'>
                  <Image
                    src='/logo/new-flower-logo.svg'
                    height={163}
                    width={150}
                    alt={`${appName} flower logo`}
                    sizes='(max-width: 768px) 100vw, 150px'
                  />
                </div>
              </NextLink>
            ) : (
              <NextLink
                href='#main-content'
                className='flex cursor-pointer items-center md:hidden'
                aria-label='Return to top of the page'
                onClick={(e) => {
                  e.preventDefault();
                  scrollToTop();
                }}
              >
                <div className='relative flex h-[55px] w-auto items-center gap-y-1 pr-2'>
                  <ThemeAwareLogo className='h-full max-w-full object-contain' />
                </div>
              </NextLink>
            )}
          </div>

          {/* Center tagline (hide on mobile */}
          <div className='hidden w-4/6 items-center justify-center xl:flex'>
            <p className={'text-center font-bold text-white transition-all duration-150 lg:text-xl xl:text-3xl'}>
              &quot;Providing Care You Can Trust&quot;
            </p>
          </div>

          {/* Right Side: Theme Toggle, Phone & Mobile Menu Toggle */}
          <div className='flex w-1/2 flex-none flex-shrink-0 items-center justify-end space-x-4 md:w-1/2 xl:w-2/6'>
            {/* Desktop Phone Info */}
            <div className={'relative mr-6 hidden flex-col items-end md:flex'}>
              <a
                href={`tel:${facilityTelephone.replace(/[^0-9]/g, '')}`}
                className={`font-serif text-3xl font-semibold ${phoneTextColorClass} transition-all duration-200`}
              >
                {facilityTelephone}
              </a>
            </div>

            {/* Mobile Menu Button */}
            <div className='flex items-center md:hidden'>
              <button
                className={`inline-flex items-center justify-center rounded-md p-2 ${textColorClass} hover:bg-primary hover:text-white focus:ring-2 focus:ring-white focus:outline-none focus:ring-inset`}
                type='button'
                aria-controls='mobile-menu'
                aria-expanded={isMobileMenuOpen}
                aria-label={isMobileMenuOpen ? 'Close main menu' : 'Open main menu'}
                onClick={handleToggleMenu}
              >
                <span className='sr-only'>{isMobileMenuOpen ? 'Close main menu' : 'Open main menu'}</span>
                {/* Icon when menu is closed */}
                <svg
                  className={`${isMobileMenuOpen ? 'hidden' : 'block'} h-6 w-6`}
                  xmlns='http://www.w3.org/2000/svg'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                  aria-hidden={true}
                >
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M4 6h16M4 12h16M4 18h16' />
                </svg>
                {/* Icon when menu is open */}
                <svg
                  className={`${isMobileMenuOpen ? 'block' : 'hidden'} h-6 w-6`}
                  xmlns='http://www.w3.org/2000/svg'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                  aria-hidden={true}
                >
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M6 18L18 6M6 6l12 12' />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Navigation */}
      <nav
        className={`bg-primary hidden shadow-md transition-all duration-300 ease-in-out md:block dark:bg-gray-800 ${navOpacityClass}`}
        aria-label='Main navigation'
      >
        <div className='container mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='flex h-12 items-center justify-center'>
            <ul className='flex space-x-1 lg:space-x-2'>
              {navItems.map((item) => (
                <li key={item.id}>
                  {isInnerPage ? (
                    <NextLink
                      href={`/#${item.id}`}
                      className='hover:text-accent dark:hover:text-primary-light inline-block cursor-pointer rounded-md px-3 py-2 text-sm font-medium text-white transition-colors duration-200'
                      onClick={removeActiveClass}
                    >
                      {item.label}
                    </NextLink>
                  ) : (
                    <NextLink
                      href={`/#${item.id}`}
                      className='hover:text-accent inline-block cursor-pointer rounded-md px-3 py-2 text-sm font-medium text-white transition-colors duration-200'
                      onClick={removeActiveClass}
                    >
                      {item.label}
                    </NextLink>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation Menu */}
      <div
        className={`${
          isMobileMenuOpen ? 'max-h-screen opacity-100' : 'pointer-events-none max-h-0 opacity-0'
        } bg-primary/95 overflow-hidden backdrop-blur-sm transition-all duration-300 ease-in-out md:hidden dark:bg-gray-800/95`}
        id='mobile-menu'
        aria-hidden={!isMobileMenuOpen}
      >
        <div className='space-y-1 px-2 pt-2 pb-3 sm:px-3'>
          <ul className='flex flex-col space-y-2' aria-label='Mobile navigation'>
            {navItems.map((item) => (
              <li key={item.id}>
                {isInnerPage ? (
                  <NextLink
                    href={`/#${item.id}`}
                    className='block cursor-pointer rounded-md px-3 py-2 text-base font-medium text-white transition-colors duration-200 hover:bg-white/10 dark:hover:bg-gray-700/50'
                    onClick={removeActiveClass}
                  >
                    {item.label}
                  </NextLink>
                ) : (
                  <NextLink
                    href={`/#${item.id}`}
                    className='block cursor-pointer rounded-md px-3 py-2 text-base font-medium text-white transition-colors duration-200 hover:bg-white/10'
                    onClick={removeActiveClass}
                    tabIndex={isMobileMenuOpen ? 0 : -1}
                  >
                    {item.label}
                  </NextLink>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </header>
  );
};

export default Header;
